// Custom binary wire protocol. All multi-byte values are little-endian.
// Encoders write into caller-provided DataViews so the server can reuse one
// buffer per room and never allocate on the tick path.
//
// The generic messages (WELCOME, EVENT, PING/PONG, SCORES, SETNAME, RESUME,
// close codes) live in @platoon/netcore and are re-exported by this
// package's index. netcore owns message ids 2, 4, 5, 6, 7, 10, 13 and
// 32-63; this game uses the rest below.
import { LE } from '@platoon/netcore';

export const MSG_INPUT = 1; // client -> server
export const MSG_SNAPSHOT = 3; // server -> client
export const MSG_SHOT = 8; // server -> client, a bullet was spawned
export const MSG_HIT = 9; // server -> shooter only, one of your bullets landed
export const MSG_SETPROP = 11; // client -> server, chosen camouflage prop
export const MSG_MATCH = 12; // server -> client, match state / clock

// Bump whenever a message layout changes. The client sends it as `?v=` on
// the WebSocket URL; a server of another version closes with CLOSE_PROTOCOL
// and the client reloads the page to fetch the matching build.
export const PROTOCOL_VERSION = 8; // 8: grenades (BTN_SWITCH, throw distance in INPUT, weapon state in SNAPSHOT, kind+dist in SHOT); 7: 7: MSG_SHOT carries the server tick (clients fast-forward remote bullets); 6: 6: warm-up state, teams/teamSize in MSG_MATCH, bot flag in SCORES, MSG_START; 5: mode + winning team; 4: room id in WELCOME; 3: level hash; 2: BTN_WALK
export const MSG_START = 14; // client -> server, "start the match now" during a warm-up

// ---- MATCH: u8 type | u8 state | u8 winner | u16 killLimit | u16 timeLimitS | u16 secondsLeft | u32 levelHash | u8 mode | u8 winnerTeam
// Sent on join, on every state change and once a second with the roster.
// `secondsLeft` counts down the current phase (match time, or intermission).
// `levelHash` identifies the level file the room runs; a client holding a
// different one fetches the level again and swaps it in place (a fix to the
// map reaches players without a page reload).
export const MATCH_SIZE = 1 + 1 + 1 + 2 + 2 + 2 + 4 + 1 + 1 + 1 + 1; // ... teams, teamSize

export function encodeMatch(
  view: DataView,
  state: number,
  winner: number,
  killLimit: number,
  timeLimitS: number,
  secondsLeft: number,
  levelHash: number,
  mode = 0,
  winnerTeam = 0,
  teams = 0,
  teamSize = 0,
): number {
  view.setUint8(0, MSG_MATCH);
  view.setUint8(1, state);
  view.setUint8(2, winner);
  view.setUint16(3, killLimit, true);
  view.setUint16(5, timeLimitS, true);
  view.setUint16(7, secondsLeft, true);
  view.setUint32(9, levelHash >>> 0, true);
  view.setUint8(13, mode);
  view.setUint8(14, winnerTeam);
  view.setUint8(15, teams);
  view.setUint8(16, teamSize);
  return MATCH_SIZE;
}

export interface MatchMsg {
  state: number;
  winner: number;
  killLimit: number;
  timeLimitS: number;
  secondsLeft: number;
  levelHash: number;
  mode: number; // MODE_DEATHMATCH | MODE_TEAM (low nibble of the wire byte)
  friendlyFire: boolean; // MATCH_FLAG_FRIENDLY_FIRE in the high nibble
  winnerTeam: number; // team mode: the winning team at intermission, 0 = draw
  teams: number; // team mode: how many teams and slots per team (the warm-up board draws empty slots)
  teamSize: number;
}

export const MATCH_FLAG_FRIENDLY_FIRE = 0x10;

export function decodeMatch(view: DataView, out: MatchMsg): boolean {
  if (view.byteLength < MATCH_SIZE) return false;
  out.state = view.getUint8(1);
  out.winner = view.getUint8(2);
  out.killLimit = view.getUint16(3, true);
  out.timeLimitS = view.getUint16(5, true);
  out.secondsLeft = view.getUint16(7, true);
  out.levelHash = view.getUint32(9, true);
  const modeByte = view.getUint8(13);
  out.mode = modeByte & 0x0f;
  out.friendlyFire = (modeByte & MATCH_FLAG_FRIENDLY_FIRE) !== 0;
  out.winnerTeam = view.getUint8(14);
  out.teams = view.getUint8(15);
  out.teamSize = view.getUint8(16);
  return true;
}

// ---- SETPROP: u8 type | u8 prop ------------------------------------------
// ---- START: u8 type (warm-up: any human may start the match) ------------
export const START_SIZE = 1;
export function encodeStart(view: DataView): number {
  view.setUint8(0, MSG_START);
  return START_SIZE;
}

export const SETPROP_SIZE = 2;

export function encodeSetProp(view: DataView, prop: number): number {
  view.setUint8(0, MSG_SETPROP);
  view.setUint8(1, prop);
  return SETPROP_SIZE;
}

// Returns the prop id, or -1 if malformed. Range checking is the server's job.
export function decodeSetProp(view: DataView): number {
  return view.byteLength >= SETPROP_SIZE ? view.getUint8(1) : -1;
}

// Game event kinds carried by MSG_EVENT (EV_JOIN/EV_LEAVE/EV_UPDATE_SOON come from netcore).
export const EV_KILL = 3; // id = victim, id2 = killer

// ---- INPUT: u8 type | u32 seq | u16 buttons | f32 aim --------------------
export const INPUT_SIZE = 1 + 4 + 2 + 4 + 1; // ... + u8 throw distance (2 px units)

export function encodeInput(view: DataView, seq: number, buttons: number, aim: number, throwDist = 0): number {
  view.setUint8(0, MSG_INPUT);
  view.setUint32(1, seq >>> 0, LE);
  view.setUint16(5, buttons, LE);
  view.setFloat32(7, aim, LE);
  view.setUint8(11, throwDist > 255 ? 255 : throwDist < 0 ? 0 : throwDist | 0);
  return INPUT_SIZE;
}

export interface InputMsg {
  seq: number;
  buttons: number;
  aim: number;
  throwDist: number; // grenade in hand: aimed distance in 2 px units
}

// Decodes into `out` to avoid allocating per message.
export function decodeInput(view: DataView, out: InputMsg): boolean {
  if (view.byteLength < INPUT_SIZE) return false;
  out.seq = view.getUint32(1, LE);
  out.buttons = view.getUint16(5, LE);
  out.aim = view.getFloat32(7, LE);
  out.throwDist = view.getUint8(11);
  return true;
}

// ---- SHOT: u8 type | u8 owner | f32 x | f32 y | f32 angle -----------------
// A bullet left the barrel. Clients spawn an identical projectile locally
// and simulate its flight with the shared stepProjectiles.
export const SHOT_SIZE = 1 + 1 + 4 * 3 + 4 + 1 + 2; // ... + u32 server tick, u8 kind, u16 grenade distance

// `tick`: the server tick the bullet was spawned on. A client receives the
// message one way-trip later and fast-forwards the bullet by the ticks that
// passed, so bullets are drawn where the server has them (docs/netcode.md
// "Projectile timing").
export function encodeShot(view: DataView, owner: number, x: number, y: number, angle: number, tick = 0, kind = 0, dist = 0): number {
  view.setUint8(0, MSG_SHOT);
  view.setUint8(1, owner);
  view.setFloat32(2, x, LE);
  view.setFloat32(6, y, LE);
  view.setFloat32(10, angle, LE);
  view.setUint32(14, tick >>> 0, LE);
  view.setUint8(18, kind);
  view.setUint16(19, dist, LE);
  return SHOT_SIZE;
}

export interface ShotMsg {
  owner: number;
  x: number;
  y: number;
  angle: number;
  tick: number;
  kind: number; // PROJ_BULLET | PROJ_GRENADE
  dist: number; // grenade: throw distance in px
}

export function decodeShot(view: DataView, out: ShotMsg): boolean {
  if (view.byteLength < SHOT_SIZE) return false;
  out.owner = view.getUint8(1);
  out.x = view.getFloat32(2, LE);
  out.y = view.getFloat32(6, LE);
  out.angle = view.getFloat32(10, LE);
  out.tick = view.getUint32(14, LE);
  out.kind = view.getUint8(18);
  out.dist = view.getUint16(19, LE);
  return true;
}

// ---- HIT: u8 type | u8 shooter | u8 victim | u8 damage --------------------
export const HIT_SIZE = 4;

export function encodeHit(view: DataView, shooter: number, victim: number, damage: number): number {
  view.setUint8(0, MSG_HIT);
  view.setUint8(1, shooter);
  view.setUint8(2, victim);
  view.setUint8(3, damage);
  return HIT_SIZE;
}

export interface HitMsg {
  shooter: number;
  victim: number;
  damage: number;
}

export function decodeHit(view: DataView, out: HitMsg): boolean {
  if (view.byteLength < HIT_SIZE) return false;
  out.shooter = view.getUint8(1);
  out.victim = view.getUint8(2);
  out.damage = view.getUint8(3);
  return true;
}

// ---- SNAPSHOT ---------------------------------------------------------------
// u8 type | u32 tick | u8 count | count * player entry
//        | u8 pickupCount | pickupCount * pickup entry
// player entry: u8 id | f32 x | f32 y | f32 aim | u32 lastSeq
//        | u8 health | u8 ammo | u8 reserve | u8 cooldown | u8 reload | u8 invuln
//        | u8 disguise | u16 camoTicks | u8 camoProp
// pickup entry: u8 slot | u8 kind | u16 x | u16 y
// The weapon fields are what the client needs to replay pending inputs
// exactly after reconciling. Pickups are render-only on the client.
export const SNAPSHOT_HEADER_SIZE = 1 + 4 + 1;
export const SNAPSHOT_ENTRY_SIZE = 1 + 4 + 4 + 4 + 4 + 6 + 4 + 4; // ... + weapon, switchTicks, grenades, throwCooldown
export const SNAPSHOT_PICKUP_SIZE = 1 + 1 + 2 + 2;

export function snapshotMaxSize(maxPlayers: number, maxPickups: number): number {
  return SNAPSHOT_HEADER_SIZE + maxPlayers * SNAPSHOT_ENTRY_SIZE + 1 + maxPickups * SNAPSHOT_PICKUP_SIZE;
}

// Writes the pickup count byte; call after the player entries.
export function writeSnapshotPickupCount(view: DataView, offset: number, count: number): number {
  view.setUint8(offset, count);
  return offset + 1;
}

export function writeSnapshotPickup(view: DataView, offset: number, slot: number, kind: number, x: number, y: number): number {
  view.setUint8(offset, slot);
  view.setUint8(offset + 1, kind);
  view.setUint16(offset + 2, x < 0 ? 0 : x > 65535 ? 65535 : Math.round(x), LE);
  view.setUint16(offset + 4, y < 0 ? 0 : y > 65535 ? 65535 : Math.round(y), LE);
  return offset + SNAPSHOT_PICKUP_SIZE;
}

export interface SnapshotPickup {
  slot: number;
  kind: number;
  x: number;
  y: number;
}

// Pickup count, given the player count from the header; -1 if malformed.
export function readSnapshotPickupCount(view: DataView, playerCount: number): number {
  const o = SNAPSHOT_HEADER_SIZE + playerCount * SNAPSHOT_ENTRY_SIZE;
  if (view.byteLength < o + 1) return -1;
  const count = view.getUint8(o);
  return view.byteLength >= o + 1 + count * SNAPSHOT_PICKUP_SIZE ? count : -1;
}

export function readSnapshotPickup(view: DataView, playerCount: number, index: number, out: SnapshotPickup): void {
  const o = SNAPSHOT_HEADER_SIZE + playerCount * SNAPSHOT_ENTRY_SIZE + 1 + index * SNAPSHOT_PICKUP_SIZE;
  out.slot = view.getUint8(o);
  out.kind = view.getUint8(o + 1);
  out.x = view.getUint16(o + 2, LE);
  out.y = view.getUint16(o + 4, LE);
}

export function writeSnapshotHeader(view: DataView, tick: number, count: number): number {
  view.setUint8(0, MSG_SNAPSHOT);
  view.setUint32(1, tick >>> 0, LE);
  view.setUint8(5, count);
  return SNAPSHOT_HEADER_SIZE;
}

// Call after all entries are written if `count` was not known up front.
export function patchSnapshotCount(view: DataView, count: number): void {
  view.setUint8(5, count);
}

export function writeSnapshotEntry(
  view: DataView,
  offset: number,
  id: number,
  x: number,
  y: number,
  aim: number,
  lastSeq: number,
  health: number,
  ammo: number,
  reserve: number,
  cooldown: number,
  reload: number,
  invuln: number,
  disguise: number,
  camoTicks: number,
  camoProp: number,
  weapon: number,
  switchTicks: number,
  grenades: number,
  throwCooldown: number,
): number {
  view.setUint8(offset, id);
  view.setFloat32(offset + 1, x, LE);
  view.setFloat32(offset + 5, y, LE);
  view.setFloat32(offset + 9, aim, LE);
  view.setUint32(offset + 13, lastSeq >>> 0, LE);
  view.setUint8(offset + 17, health);
  view.setUint8(offset + 18, ammo);
  view.setUint8(offset + 19, reserve);
  view.setUint8(offset + 20, cooldown);
  view.setUint8(offset + 21, reload);
  view.setUint8(offset + 22, invuln);
  view.setUint8(offset + 23, disguise);
  view.setUint16(offset + 24, camoTicks, LE);
  view.setUint8(offset + 26, camoProp);
  view.setUint8(offset + 27, weapon);
  view.setUint8(offset + 28, switchTicks);
  view.setUint8(offset + 29, grenades);
  view.setUint8(offset + 30, throwCooldown);
  return offset + SNAPSHOT_ENTRY_SIZE;
}

export interface SnapshotHeader {
  tick: number;
  count: number;
}

export function readSnapshotHeader(view: DataView, out: SnapshotHeader): boolean {
  if (view.byteLength < SNAPSHOT_HEADER_SIZE) return false;
  out.tick = view.getUint32(1, LE);
  out.count = view.getUint8(5);
  return view.byteLength >= SNAPSHOT_HEADER_SIZE + out.count * SNAPSHOT_ENTRY_SIZE;
}

export interface SnapshotEntry {
  id: number;
  x: number;
  y: number;
  aim: number;
  lastSeq: number;
  health: number;
  ammo: number;
  reserve: number;
  cooldown: number;
  reload: number;
  invuln: number;
  disguise: number; // prop shown to others right now, or 0
  camoTicks: number; // charge remaining (meaningful for yourself)
  camoProp: number; // chosen prop, 0 = none yet
  weapon: number; // WEAPON_RIFLE | WEAPON_GRENADE (drawn for everyone)
  switchTicks: number;
  grenades: number;
  throwCooldown: number;
}

export function readSnapshotEntry(view: DataView, index: number, out: SnapshotEntry): void {
  const o = SNAPSHOT_HEADER_SIZE + index * SNAPSHOT_ENTRY_SIZE;
  out.id = view.getUint8(o);
  out.x = view.getFloat32(o + 1, LE);
  out.y = view.getFloat32(o + 5, LE);
  out.aim = view.getFloat32(o + 9, LE);
  out.lastSeq = view.getUint32(o + 13, LE);
  out.health = view.getUint8(o + 17);
  out.ammo = view.getUint8(o + 18);
  out.reserve = view.getUint8(o + 19);
  out.cooldown = view.getUint8(o + 20);
  out.reload = view.getUint8(o + 21);
  out.invuln = view.getUint8(o + 22);
  out.disguise = view.getUint8(o + 23);
  out.camoTicks = view.getUint16(o + 24, LE);
  out.camoProp = view.getUint8(o + 26);
  out.weapon = view.getUint8(o + 27);
  out.switchTicks = view.getUint8(o + 28);
  out.grenades = view.getUint8(o + 29);
  out.throwCooldown = view.getUint8(o + 30);
}
